import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";
import { NOTIFICATIONS_UPDATED_EVENT } from "@/components/Layout";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unread

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: filter === "unread" } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [filter]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All notifications marked as read");
    load();
    window.dispatchEvent(new Event(NOTIFICATIONS_UPDATED_EVENT));
  };

  return (
    <div className="p-6 sm:p-10 max-w-3xl mx-auto page-enter">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Inbox</div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight mt-1">Notifications</h1>
        </div>
        <Button data-testid="mark-all-read" variant="outline" size="sm" onClick={markAll}><CheckCheck className="w-4 h-4 mr-1" />Mark all read</Button>
      </div>

      <div className="mt-6 flex gap-2">
        <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")} data-testid="filter-all">All</Button>
        <Button variant={filter === "unread" ? "default" : "outline"} size="sm" onClick={() => setFilter("unread")} data-testid="filter-unread">Unread</Button>
      </div>

      <div className="mt-6 space-y-2">
        {items.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No notifications.</div>}
        {items.map((n) => (
          <Card key={n.id} className={n.read ? "border-border" : "border-primary/40 bg-primary/5"}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-primary" />
                    <div className="font-semibold text-sm">{n.title}</div>
                    {!n.read && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">NEW</Badge>}
                  </div>
                  <div className="text-sm text-slate-700 mt-1">{n.message}</div>
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                    <span>{new Date(n.created_at).toLocaleString()}</span>
                    {n.program_id && <Link to={`/programs/${n.program_id}`} className="text-primary hover:underline">Open program →</Link>}
                  </div>
                </div>
                {!n.read && (
                  <Button variant="ghost" size="sm" data-testid={`mark-read-${n.id}`} onClick={async () => {
                    await api.post(`/notifications/${n.id}/read`);
                    load();
                    window.dispatchEvent(new Event(NOTIFICATIONS_UPDATED_EVENT));
                  }}>
                    Mark read
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
